import React, {
  Fragment,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Select from "react-select";
import {
  AddIcon,
  FilterIcon,
  EditIcon,
  DeleteIcon,
  ResetIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  ApproveIcon,
  Disapprove,
} from "../../../assets/icons/index";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { Menu, Transition } from "@headlessui/react";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import Constant from "../../../utils/constant";
import DeleteModal from "../../shared/common/deleteModal";
import Loader from "../../shared/loader/loader";
import Tooltip from "../../shared/common/ellipsisText";
import { Link, useNavigate } from "react-router";
import { ISlide } from "../../../model/interface/slide";
import { toast } from "react-toastify";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import MultiStateButton from "../../shared/common/multiStateButton";
import { getSlides, deleteSlide } from "../../../api/slide.service";
import { getContentArea } from "../../../api/contentArea.service";
import { ISelectElement } from "../../../model/interface/keyValuePair";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ACTIVE_MULTI_STATE = [
  { label: "Active", value: true },
  { label: "Both", value: null, selected: true },
  { label: "Inactive", value: false },
] as const;

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 10;

const gradeLevelData = [
  {
    value: "ece",
    name: "Early Childhood Learning",
    label: "Early Childhood Learning (ece)",
  },
  { value: "elem", name: "Elementary", label: "Elementary (elem)" },
  { value: "ms", name: "Middle School", label: "Middle School (ms)" },
  { value: "hs", name: "High School", label: "High School (hs)" },
];

interface ISlideFilter {
  slideId: string;
  title: string;
  description: string;
  link: string;
  standardId: string;
  standardName: string;
  isActive: boolean | null;
  gradeLevel: string;
  contentAreaId: number;
}

interface ISortState {
  columnName: string;
  sortDirection: "ASC" | "DESC";
}

const initialFilter: ISlideFilter = {
  slideId: "",
  title: "",
  description: "",
  link: "",
  standardId: "",
  standardName: "",
  isActive: null,
  gradeLevel: "",
  contentAreaId: 0,
};

const initialSortState: ISortState = {
  columnName: "SlideId",
  sortDirection: "ASC",
};

export default function Slides() {
  const navigate = useNavigate();

  const [slides, setSlides] = useState<ISlide[]>([]);
  const [filter, setFilter] = useState<ISlideFilter>(initialFilter);
  const [loading, setLoading] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);
  const [resetDropdownFlag, setResetDropdownFlag] = useState<boolean>(false);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [sorting, setSorting] = useState<ISortState>(initialSortState);
  const [contentAreas, setContentAreas] = useState<ISelectElement[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isFilter, setIsFilter] = useState<boolean>(false);

  const paginationRef = useRef<any>(null);
  const pageSizeRef = useRef<any>(null);

  useEffect(() => {
    bindContentAreas();
  }, []);

  useEffect(() => {
    if (isLoaded && !isFilter) {
      fetchSlideData();
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === 1 && !isFilter) {
      fetchSlideData();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  async function bindContentAreas() {
    try {
      const response = await getContentArea();
      const result = response.data.data;
      const contentAreaData = result.map((item) => ({
        label: item.name,
        value: item.contentAreaId,
      }));
      setContentAreas(contentAreaData);
    } catch (error) {
      console.error("Error fetching content areas:", error);
    }
  }

  const fetchSlideData = useCallback(
    async (isReset?: boolean, resetObj?: any, onFilter?: boolean) => {
      try {
        setLoading(true);
        if (onFilter) setIsFilter(true);

        const slideFilter: ISlide = {
          slideId: filter.slideId ? parseInt(filter.slideId) : 0,
          standardId: filter.standardId ? parseInt(filter.standardId) : 0,
          title: filter.title || "",
          description: filter.description || "",
          link: filter.link || "",
          isActive: isActive,
          standardName: filter.standardName || "",
          gradeLevel: filter.gradeLevel || "",
          contentAreaId: filter.contentAreaId || 0,
        };

        const response = await getSlides(
          isReset ? resetObj : slideFilter,
          onFilter ? DEFAULT_PAGE_SIZE : pageSize,
          onFilter ? DEFAULT_PAGE_NUMBER : currentPage,
          sorting.columnName,
          sorting.sortDirection
        );

        if (response.data) {
          setSlides(response.data.data || []);
          setTotalRecord(response.data.totalRecords || 0);
        }

        if (onFilter) {
          paginationRef?.current?.onReset();
          pageSizeRef?.current?.onReset();
          setIsFilter(false);
        }
      } catch (error) {
        console.error("Error fetching slides:", error);
        toast.error("Failed to fetch slides");
        setSlides([]);
        setTotalRecord(0);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, sorting, filter, isActive]
  );

  const handleFilter = () => {
    fetchSlideData(false, false, true);
  };

  const handleReset = () => {
    setLoading(true);
    setResetDropdownFlag(!resetDropdownFlag);
    setFilter(initialFilter);
    setIsActive(undefined);

    fetchSlideData(true, {
      slideId: 0,
      standardId: 0,
      title: "",
      description: "",
      link: "",
      isActive: undefined,
      standardName: "",
      gradeLevel: "",
      contentAreaId: 0,
    });

    setTimeout(() => {
      setResetDropdownFlag(false);
      setLoading(false);
    }, 1500);

    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
  };

  const handleSort = useCallback(
    (columnName: string) => {
      setSorting((prev) => {
        const newSortDirection: "ASC" | "DESC" =
          prev.columnName === columnName && prev.sortDirection === "ASC"
            ? "DESC"
            : "ASC";
        return { columnName, sortDirection: newSortDirection };
      });

      setLoading(true);
      fetchSlideData();
    },
    [fetchSlideData]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;

    try {
      await deleteSlide(deleteId);
      toast.success("Slide deleted successfully");
      setDeleteModal(false);
      setDeleteId(0);
      await fetchSlideData();
    } catch (error) {
      console.error("Error deleting slide:", error);
      toast.error("Failed to delete slide");
    }
  }, [deleteId, fetchSlideData]);

  const handleEditSlide = useCallback(
    (slideId: number) => {
      navigate(Constant.SLIDEEDIT.replace(":id", slideId.toString()));
    },
    [navigate]
  );

  const handleAddSlide = useCallback(() => {
    navigate(Constant.ADDSLIDE);
  }, [navigate]);

  const handleFilterChange = (value: any, field: keyof ISlideFilter) => {
    setFilter((prev) => ({ ...prev, [field]: value }));
  };

  const openDeleteModal = useCallback((slideId: number) => {
    setDeleteId(slideId);
    setDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModal(false);
    setDeleteId(0);
  }, []);

  const renderSortIcon = (columnName: string) => {
    const isActive = sorting.columnName === columnName;
    if (!isActive || sorting.sortDirection === "DESC") {
      return <CaretDownSolidIcon className="w-5 h-5" />;
    }
    return <CaretUpSolidIcon className="w-5 h-5" />;
  };

  return (
    <AllowedTo perform={Permissions.slide_view} no={() => <AccessDenied />}>
      {loading && <Loader />}

      {deleteModal && (
        <DeleteModal
          closeDeleteModal={closeDeleteModal}
          deleteRecord={handleDelete}
        />
      )}

      <div className="flex-1">
        <div className="h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8 py-8 pt-0 pb-14 mt-7">
          <div className="relative py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>

            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Slide</h3>
              <AllowedTo perform={Permissions.slide_create}>
                <button
                  onClick={handleAddSlide}
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transition ease-in-out duration-300"
                >
                  <AddIcon className="w-5" />
                  Add Slide
                </button>
              </AllowedTo>
            </div>

            {/* Filter Section */}
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Slide Id</label>
                  <div className="contentArea mt-1">
                    <input
                      id="slideId"
                      name="slideId"
                      value={filter.slideId}
                      onChange={(e) =>
                        handleFilterChange(e.target.value, "slideId")
                      }
                      type="text"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    />
                  </div>
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Title</label>
                  <div className="contentArea mt-1">
                    <input
                      id="title"
                      name="title"
                      value={filter.title}
                      onChange={(e) =>
                        handleFilterChange(e.target.value, "title")
                      }
                      type="text"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    />
                  </div>
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Link</label>
                  <div className="contentArea mt-1">
                    <input
                      id="link"
                      name="link"
                      value={filter.link}
                      onChange={(e) =>
                        handleFilterChange(e.target.value, "link")
                      }
                      type="text"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    />
                  </div>
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Standard Id</label>
                  <div className="contentArea mt-1">
                    <input
                      id="standardId"
                      name="standardId"
                      value={filter.standardId}
                      onChange={(e) =>
                        handleFilterChange(e.target.value, "standardId")
                      }
                      type="number"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    />
                  </div>
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Standard Name</label>
                  <div className="contentArea mt-1">
                    <input
                      id="standardName"
                      name="standardName"
                      value={filter.standardName}
                      onChange={(e) =>
                        handleFilterChange(e.target.value, "standardName")
                      }
                      type="text"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    />
                  </div>
                </div>

                <div className="col-span-2 flex items-center mt-6">
                  <MultiStateButton
                    labels={ACTIVE_MULTI_STATE}
                    onSelect={(e) => setIsActive(e.value)}
                    onReset={resetDropdownFlag}
                  />
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Grade Level</label>
                  <Select
                    isMulti={true}
                    className="mt-1 text-sm"
                    options={gradeLevelData}
                    onChange={(e) => {
                      const values = e
                        ? e.map((item) => item.value).join(", ")
                        : "";
                      handleFilterChange(values, "gradeLevel");
                    }}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary25: "#B3B3B3",
                        primary: "#041E42",
                      },
                    })}
                    styles={{
                      control: (base) => ({
                        ...base,
                        background:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "#1A1C23"
                            : "white",
                        borderColor:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "#6B7280"
                            : "#D1D5DB",
                        borderWidth: "1px",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "white"
                            : "#374051",
                      }),
                      input: (base) => ({
                        ...base,
                        color:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "white"
                            : "",
                      }),
                    }}
                  />
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Content Area</label>
                  <Select
                    options={contentAreas}
                    className="mt-1 text-sm"
                    isSearchable={true}
                    onChange={(e) =>
                      handleFilterChange(e?.value || 0, "contentAreaId")
                    }
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary25: "#B3B3B3",
                        primary: "#041E42",
                      },
                    })}
                    styles={{
                      control: (base) => ({
                        ...base,
                        background:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "#1A1C23"
                            : "white",
                        borderColor:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "#6B7280"
                            : "#D1D5DB",
                        borderWidth: "1px",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "white"
                            : "#374051",
                      }),
                      input: (base) => ({
                        ...base,
                        color:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "white"
                            : "",
                      }),
                    }}
                  />
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="mt-4">
                <button
                  type="submit"
                  className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  onClick={handleFilter}
                >
                  <FilterIcon className="w-3 h-3" />
                  <span>Filter</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                >
                  <ResetIcon className="w-3 h-3" />
                  <span>Reset Filter</span>
                </button>
              </div>
            </div>

            {/* Table Section */}
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-5 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider cursor-pointer"
                              onClick={() => handleSort("SlideId")}
                            >
                              <span className="flex">
                                Id
                                <span className="cursor-pointer">
                                  {renderSortIcon("SlideId")}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider cursor-pointer"
                              onClick={() => handleSort("Title")}
                            >
                              <span className="flex">
                                Title
                                <span className="cursor-pointer">
                                  {renderSortIcon("Title")}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Link</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Description</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {slides.map((slide, index) => (
                            <tr
                              key={slide.slideId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500 relative cursor-pointer">
                                {slide.isActive ? (
                                  <span className="mr-1 mt-2 h-full w-0.5 bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                ) : (
                                  <span className="mr-1 mt-2 h-full w-0.5 bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                )}
                                <Link
                                  to={Constant.SLIDEEDIT.replace(
                                    ":id",
                                    slide.slideId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {slide.slideId}
                                </Link>
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <Link
                                  to={Constant.SLIDEEDIT.replace(
                                    ":id",
                                    slide.slideId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {slide.title}
                                </Link>
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <a
                                  rel="noreferrer"
                                  href={slide.link}
                                  target="_blank"
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  <Tooltip text={slide.link} length={40} />
                                </a>
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <Tooltip
                                  text={slide.description || "-"}
                                  length={50}
                                />
                              </td>
                              <td className="flex justify-end items-center px-3 py-2 whitespace-wrap text-sm text-center font-normal text-gray-700">
                                <div className="flex space-x-2 justify-center items-center">
                                  {slide.isActive ? (
                                    <ApproveIcon title="Active" />
                                  ) : (
                                    <Disapprove title="Inactive" />
                                  )}

                                  <Menu
                                    as="div"
                                    className="relative flex justify-end items-center"
                                  >
                                    <Menu.Button className="h-7 bg-white inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bg-primary-color">
                                      <span className="sr-only">
                                        Open options
                                      </span>
                                      <DotsVerticalIcon
                                        className="w-5 h-5"
                                        aria-hidden="true"
                                      />
                                    </Menu.Button>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items className="mx-3 origin-top-right absolute right-7 top-0 w-48 mt-1 rounded-md shadow-lg z-10 bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
                                        <AllowedTo
                                          perform={Permissions.slide_modify}
                                        >
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={() =>
                                                    handleEditSlide(
                                                      slide.slideId
                                                    )
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm cursor-pointer"
                                                  )}
                                                >
                                                  <EditIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transition ease-in-out duration-300" />
                                                  Edit
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                        </AllowedTo>

                                        <AllowedTo
                                          perform={Permissions.slide_delete}
                                        >
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={() =>
                                                    openDeleteModal(
                                                      slide.slideId
                                                    )
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm cursor-pointer"
                                                  )}
                                                >
                                                  <DeleteIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transition ease-in-out duration-300" />
                                                  Delete
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                        </AllowedTo>
                                      </Menu.Items>
                                    </Transition>
                                  </Menu>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                <div
                  className={`flex justify-between items-center ${
                    slides.length === 0 ? "hidden" : ""
                  }`}
                >
                  <Pagination
                    pages={Math.ceil(totalRecord / pageSize)}
                    setPageNumber={setCurrentPage}
                    ref={paginationRef}
                  />
                  <span>
                    <span className="text-sm pr-4 ml-auto text-left-700">
                      Showing {currentPage} to{" "}
                      {totalRecord < pageSize ? totalRecord : pageSize} of{" "}
                      {totalRecord} results
                    </span>
                    <SelectPageSize
                      setPageSize={setPageSize}
                      type={"second"}
                      ref={pageSizeRef}
                    />
                  </span>
                </div>

                {/* Empty State */}
                {slides.length === 0 && !loading && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
}
